import { Fragment, useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, ListChecks, Plus, RefreshCw, Trash2 } from "lucide-react";

const STATUTS = [
  ["", "Toutes"], ["en_attente", "En attente"], ["executee", "Exécutées"], ["echec", "Refusées"], ["annulee", "Annulées"],
];
const BADGE = {
  en_attente: "bg-amber-100 text-amber-800", en_cours: "bg-amber-100 text-amber-800", executee: "bg-emerald-100 text-emerald-800",
  echec: "bg-red-100 text-red-700", annulee: "bg-slate-100 text-slate-600",
};
const LABEL = { en_attente: "En attente", en_cours: "En cours", executee: "Exécutée", echec: "Refusée", annulee: "Annulée" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

function Sessions({ sessions, activeId, onOpen, onNew, onDelete }) {
  return (
    <div className="space-y-1">
      <button onClick={onNew} data-testid="lili-new-session" className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-dashed border-slate-300 hover:border-[#C5A059] text-slate-600">
        <Plus className="h-4 w-4" />Nouvelle conversation
      </button>
      {sessions.length === 0 && <p className="text-xs text-slate-400 px-1 pt-2" data-testid="lili-sessions-empty">Aucune conversation.</p>}
      {sessions.map((s) => (
        <div key={s.session_id} className="group relative">
          <button onClick={() => onOpen(s.session_id)} data-testid={`lili-session-${s.session_id}`}
                  className={`w-full text-left px-3 py-2 pr-8 rounded-md text-sm transition-colors ${activeId === s.session_id ? "bg-[#C5A059]/10 text-slate-900" : "hover:bg-slate-50 text-slate-700"}`}>
            <div className="truncate">{s.titre || "Conversation"}</div>
            <div className="text-[11px] text-slate-400">{fmt(s.fin)} · {s.messages} messages</div>
          </button>
          <button type="button" onClick={() => onDelete(s.session_id)} title="Supprimer la conversation et ses pièces jointes" data-testid={`lili-session-delete-${s.session_id}`}
                  className="absolute right-2 top-2.5 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  );
}

function ActionDetail({ action, onClose }) {
  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lili-history-detail">
        <DialogHeader>
          <DialogTitle>{action?.libelle || action?.tool.replaceAll("_", " ")}</DialogTitle>
          <DialogDescription>{action?.description}</DialogDescription>
        </DialogHeader>
        {action && (
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-slate-500">Statut</dt><dd>{LABEL[action.statut] || action.statut}{action.http ? ` (HTTP ${action.http})` : ""}</dd>
            <dt className="text-slate-500">Par</dt><dd>{action.user_email || "—"}</dd>
            <dt className="text-slate-500">Proposée</dt><dd>{fmt(action.created_at)}</dd>
            <dt className="text-slate-500">Exécutée</dt><dd>{fmt(action.executed_at || action.cancelled_at)}</dd>
            {action.motif && <><dt className="text-slate-500">Motif</dt><dd>{action.motif}</dd></>}
            {Object.entries(action.args || {}).map(([k, v]) => (<Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="break-all">{String(v)}</dd></Fragment>))}
            {action.statut === "echec" && <><dt className="text-slate-500">Erreur</dt><dd className="text-red-700">{formatApiError(action.resultat?.detail) || "—"}</dd></>}
          </dl>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Actions() {
  const { user } = useAuth();
  const [statut, setStatut] = useState("");
  const [tous, setTous] = useState(false);
  const [data, setData] = useState({ items: [], counts: {} });
  const [detail, setDetail] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/lili/actions", { params: { statut: statut || undefined, tous } }); setData(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, [statut, tous]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-2" data-testid="lili-actions-history">
      <div className="flex flex-wrap gap-1">
        {STATUTS.map(([v, l]) => (
          <button key={v || "all"} onClick={() => setStatut(v)} data-testid={`lili-actions-filter-${v || "all"}`}
                  className={`text-[11px] px-2 py-1 rounded-full border ${statut === v ? "bg-[#C5A059] text-white border-[#C5A059]" : "border-slate-200 text-slate-600"}`}>
            {l}{v && data.counts[v] ? ` ${data.counts[v]}` : ""}
          </button>
        ))}
        <button onClick={load} title="Rafraîchir" data-testid="lili-actions-refresh" className="ml-auto text-slate-400 hover:text-slate-700"><RefreshCw className="h-3.5 w-3.5" /></button>
      </div>
      {user?.role === "admin" && (
        <label className="flex items-center gap-2 text-[11px] text-slate-600">
          <input type="checkbox" checked={tous} onChange={(e) => setTous(e.target.checked)} data-testid="lili-actions-tous" />Toutes les personnes
        </label>
      )}
      {data.items.length === 0 && <p className="text-xs text-slate-400" data-testid="lili-actions-empty">Aucune action.</p>}
      {data.items.map((a) => (
        <button key={a.id} onClick={() => setDetail(a)} data-testid={`lili-history-action-${a.id}`} className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 border border-slate-100">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-slate-800 truncate">{a.libelle || a.tool.replaceAll("_", " ")}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${BADGE[a.statut] || ""}`}>{LABEL[a.statut] || a.statut}</span>
          </div>
          <div className="text-[11px] text-slate-400">{fmt(a.created_at)}{tous ? ` · ${a.user_email || ""}` : ""}</div>
        </button>
      ))}
      <ActionDetail action={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

export function LiliHistory({ activeId, onOpen, onNew, refreshKey }) {
  const [tab, setTab] = useState("sessions");
  const [sessions, setSessions] = useState([]);
  const load = useCallback(() => api.get("/lili/sessions").then((r) => setSessions(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [refreshKey, load]);
  return (
    <aside className="w-72 shrink-0 bg-white border border-slate-200 rounded-xl p-3 overflow-y-auto hidden lg:block" data-testid="lili-history">
      <div className="flex gap-1 mb-3">
        <button onClick={() => setTab("sessions")} data-testid="lili-tab-sessions" className={`flex-1 text-xs py-1.5 rounded-md flex items-center justify-center gap-1 ${tab === "sessions" ? "bg-[#17352e] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <MessageSquare className="h-3.5 w-3.5" />Conversations
        </button>
        <button onClick={() => setTab("actions")} data-testid="lili-tab-actions" className={`flex-1 text-xs py-1.5 rounded-md flex items-center justify-center gap-1 ${tab === "actions" ? "bg-[#17352e] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <ListChecks className="h-3.5 w-3.5" />Actions
        </button>
      </div>
      {tab === "sessions" ? <Sessions sessions={sessions} activeId={activeId} onOpen={onOpen} onNew={onNew} onDelete={async (sid) => {
        if (!window.confirm("Supprimer cette conversation et ses pièces jointes ?")) return;
        try { await api.delete(`/lili/sessions/${sid}`); toast.success("Conversation supprimée"); if (sid === activeId) onNew(); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
      }} /> : <Actions key={refreshKey} />}
    </aside>
  );
}
